// frontend/src/components/ClientModal.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import clientExtraFieldService from '../services/clientExtraFieldService';

export default function ClientModal({ show, onHide, onSave, initialData = {} }) {
  const EMPTY_CLIENT = useMemo(() => ({
    id: undefined,
    firstName: '',
    lastName: '',
    nifCin: '',
    email: '',
    phone: '',
    isMinor: false,
    tutorName: '',
    tutorRelation: '',
    // on ne met PAS extra ici pour laisser buildDefaults le gérer
  }), []);

  const [extraFields, setExtraFields] = useState([]);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setError,
    clearErrors,
    formState: { errors, isSubmitting }
  } = useForm({
    mode: 'onTouched',
    defaultValues: EMPTY_CLIENT,
    shouldUnregister: true // <- clé pour oublier les champs démontés
  });

  // Charge la définition des champs dynamiques à l’ouverture
  useEffect(() => {
    if (!show) return;
    clientExtraFieldService
      .listActive()
      .then(setExtraFields)
      .catch(() => setExtraFields([]));
  }, [show]);

  // Construit les valeurs par défaut pour reset()
  const buildDefaults = (base, data, fields) => {
    const values = { ...base, ...data };
    // on mappe les champs extra vers __extra__<code>
    const extra = (data && data.extra) ? data.extra : {};
    for (const f of fields) {
      const key = `__extra__${f.code}`;
      let v = extra?.[f.code];
      // normalise selon type
      if (f.type === 'boolean') {
        v = !!v;
      } else if (f.type === 'number') {
        v = (v === undefined || v === null || v === '') ? '' : Number(v);
      } else {
        v = (v === undefined || v === null) ? '' : String(v);
      }
      values[key] = v;
    }
    return values;
  };

  // Reset complet à l’ouverture ET quand initialData/extraFields changent
  useEffect(() => {
    if (!show) {
      reset(EMPTY_CLIENT); // ferme → vide
      clearErrors();
      return;
    }
    // ouvert → reset aux bonnes valeurs
    const defaults = buildDefaults(EMPTY_CLIENT, initialData, extraFields);
    reset(defaults);
    clearErrors();
  }, [show, initialData, extraFields, reset, clearErrors, EMPTY_CLIENT]);

  const isMinor = watch('isMinor');

  const submit = async (values) => {
    // Récupérer les valeurs dynamiques et les normaliser
    const extra = {};
    for (const f of extraFields) {
      const key = `__extra__${f.code}`;
      const raw = values[key];

      let out = raw;
      if (f.type === 'boolean') out = !!raw;
      if (f.type === 'number' && raw !== '' && raw != null) out = Number(raw);
      if (f.type !== 'boolean' && (raw === undefined || raw === '')) out = null;

      extra[f.code] = out === undefined ? null : out;
    }

    // Construire le payload vers l’API (et retirer les __extra__)
    const payload = {
      ...values,
      tutorName: values.isMinor ? values.tutorName : '',
      tutorRelation: values.isMinor ? values.tutorRelation : '',
      extra
    };
    Object.keys(payload).forEach(k => { if (k.startsWith('__extra__')) delete payload[k]; });

    try {
      await onSave(payload);            // doit throw en cas d’erreur HTTP
      // succès → on nettoie pour la prochaine création
      const defaults = buildDefaults(EMPTY_CLIENT, {}, extraFields);
      reset(defaults);
      clearErrors();
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;

      // Collision (409) → marquer précisément les champs concernés + bannière
      if (status === 409 && data?.error === 'CLIENT_DUPLICATE_FIELDS') {
        const f = data.fields || {};
        const msgs = [];
        if (f.nifCin) {
          setError('nifCin', { type: 'server', message: f.nifCin });
          msgs.push(f.nifCin);
        }
        if (f.email) {
          setError('email', { type: 'server', message: f.email });
          msgs.push(f.email);
        }
        if (msgs.length) {
          setError('root', { type: 'server', message: msgs.join(' · ') });
        }
        return; // laisser l’utilisateur corriger
      }

      // Autres erreurs → message générique sur le formulaire
      setError('root', { type: 'server', message: 'Une erreur est survenue. Réessayez.' });
    }
  };

  return (
    <Modal show={show} onHide={onHide} backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>{initialData?.id ? 'Modifier un client' : 'Créer un client'}</Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit(submit)} noValidate>
        <Modal.Body>
          {errors.root?.message && (
            <div className="alert alert-danger" role="alert">
              {errors.root.message}
            </div>
          )}

          <Row className="g-3">
            <Col md={6}>
              <Form.Group controlId="firstName">
                <Form.Label>Prénom *</Form.Label>
                <Form.Control
                  {...register('firstName', {
                    required: 'Requis',
                    maxLength: { value: 50, message: 'Max 50 caractères' }
                  })}
                />
                {errors.firstName && (
                  <Form.Text className="text-danger">
                    {errors.firstName.message}
                  </Form.Text>
                )}
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="lastName">
                <Form.Label>Nom *</Form.Label>
                <Form.Control
                  {...register('lastName', {
                    required: 'Requis',
                    maxLength: { value: 50, message: 'Max 50 caractères' }
                  })}
                />
                {errors.lastName && (
                  <Form.Text className="text-danger">
                    {errors.lastName.message}
                  </Form.Text>
                )}
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mt-3" controlId="nifCin">
                <Form.Label>NIF/CIN *</Form.Label>
                <Form.Control
                  {...register('nifCin', {
                    required: 'Requis',
                    pattern: {
                      value: /^[0-9A-Za-z-]{9,20}$/,
                      message: 'Format invalide'
                    }
                  })}
                />
                {errors.nifCin && (
                  <Form.Text className="text-danger">
                    {errors.nifCin.message}
                  </Form.Text>
                )}
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mt-3" controlId="email">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  {...register('email', {
                    pattern: {
                      value: /^\S+@\S+\.\S+$/,
                      message: 'Email invalide'
                    }
                  })}
                />
                {errors.email && (
                  <Form.Text className="text-danger">
                    {errors.email.message}
                  </Form.Text>
                )}
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mt-3" controlId="phone">
                <Form.Label>Téléphone</Form.Label>
                <Form.Control
                  {...register('phone', {
                    pattern: {
                      value: /^\+?[0-9]{8,15}$/,
                      message: 'Doit être 8–15 chiffres'
                    }
                  })}
                />
                {errors.phone && (
                  <Form.Text className="text-danger">
                    {errors.phone.message}
                  </Form.Text>
                )}
              </Form.Group>
            </Col>

           {/*<Col md={6}>
              <Form.Group className="mt-3" controlId="isMinor">
                <Form.Check type="switch" label="Client mineur" {...register('isMinor')} />
              </Form.Group>
            </Col>*/}

            {watch('isMinor') && (
              <>
                <Col md={6}>
                  <Form.Group className="mt-3" controlId="tutorName">
                    <Form.Label>Nom du tuteur *</Form.Label>
                    <Form.Control
                      {...register('tutorName', {
                        required: 'Requis pour un mineur',
                        maxLength: { value: 80, message: 'Max 80 caractères' }
                      })}
                    />
                    {errors.tutorName && (
                      <Form.Text className="text-danger">
                        {errors.tutorName.message}
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mt-3" controlId="tutorRelation">
                    <Form.Label>Lien avec le tuteur *</Form.Label>
                    <Form.Control
                      {...register('tutorRelation', {
                        required: 'Requis pour un mineur',
                        maxLength: { value: 60, message: 'Max 60 caractères' }
                      })}
                    />
                    {errors.tutorRelation && (
                      <Form.Text className="text-danger">
                        {errors.tutorRelation.message}
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>
              </>
            )}
          </Row>

          {/* --- SECTION: CHAMPS SUPPLÉMENTAIRES (dynamiques) --- */}
          {extraFields.length > 0 && (
            <>
              <hr className="mt-4" />
              <h6 className="mb-3">Champs supplémentaires</h6>
              <Row className="g-3">
                {extraFields.map((f) => (
                  <Col md={6} key={f.id}>
                    <Form.Group controlId={`extra-${f.code}`}>
                      <Form.Label>
                        {f.label} {f.required ? '*' : ''}
                      </Form.Label>

                      {f.type === 'select' ? (
                        <Form.Select
                          {...register(`__extra__${f.code}`, {
                            required: f.required ? 'Requis' : false
                          })}
                        >
                          <option value="">—</option>
                          {(f.options?.choices || []).map((opt) => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </Form.Select>
                      ) : f.type === 'boolean' ? (
                        <Form.Check
                          type="switch"
                          label=""
                          {...register(`__extra__${f.code}`)}
                        />
                      ) : (
                        <Form.Control
                          type={
                            f.type === 'number' ? 'number' :
                            f.type === 'date' ? 'date' : 'text'
                          }
                          {...register(`__extra__${f.code}`, {
                            required: f.required ? 'Requis' : false
                          })}
                        />
                      )}

                      {errors[`__extra__${f.code}`] && (
                        <Form.Text className="text-danger">
                          {errors[`__extra__${f.code}`]?.message}
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Col>
                ))}
              </Row>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Annuler
          </Button>
          <Button type="submit" variant="primary" disabled={isSubmitting}>
            {initialData?.id ? 'Enregistrer' : 'Créer'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
